import React from 'react';
import { Clock, Heart, Users } from 'lucide-react';

const AnticipatorGrief = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-purple-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
            <Clock className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Understanding Anticipatory Grief
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Grief doesn't always begin after a loss. Sometimes it starts before—when we know 
            loss is coming.
          </p>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-8 items-center mb-12">
          {/* Image */}
          <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
            <img 
              src="https://images.unsplash.com/flagged/photo-1569744068983-6dfc2f27deb8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxhbnRpY2lwYXRvcnklMjBncmllZnxlbnwwfHx8fDE3NzM5MjQxNzN8MA&ixlib=rb-4.1.0&q=85"
              alt="Person in contemplation by water"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/40 to-transparent"></div>
          </div>

          {/* Description */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">What is Anticipatory Grief?</h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              Anticipatory grief is the grief we experience before an impending loss. It occurs 
              when we know someone we love is dying, or when we're facing our own terminal diagnosis. 
              This type of grief is just as real and valid as grief after loss.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              Unlike traditional grief, anticipatory grief allows us time to prepare emotionally, 
              say important things, resolve conflicts, and begin processing our feelings while our 
              loved one is still alive.
            </p>
          </div>
        </div>

        {/* Common Experiences */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <Heart className="w-6 h-6 text-purple-600" />
            Common Experiences with Anticipatory Grief
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Emotional Rollercoaster</h4>
                  <p className="text-gray-700">Fluctuating between hope, despair, anger, and acceptance as the situation changes</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Exhaustion</h4>
                  <p className="text-gray-700">Physical and emotional fatigue from caregiving and processing difficult emotions</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Guilt</h4>
                  <p className="text-gray-700">Feeling guilty for grieving while the person is still alive, or for moments of relief</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Anticipating the Future</h4>
                  <p className="text-gray-700">Imagining life without your loved one and feeling the pain of that future loss</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Increased Anxiety</h4>
                  <p className="text-gray-700">Worry about the dying process, suffering, and what comes after</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Unfinished Business</h4>
                  <p className="text-gray-700">Urgency to say important things, resolve conflicts, or create final memories</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Social Isolation</h4>
                  <p className="text-gray-700">Others may not understand this type of grief, leading to feelings of loneliness</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span className="text-purple-600 text-xl">•</span>
                <div>
                  <h4 className="font-semibold text-gray-900">Contradictory Feelings</h4>
                  <p className="text-gray-700">Wanting more time while also wanting suffering to end</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* How to Cope */}
        <div className="bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl p-8 md:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Users className="w-8 h-8" />
            How to Cope with Anticipatory Grief
          </h3>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Allow yourself to grieve now</strong> - Your feelings are valid even though loss hasn't occurred yet</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Stay present</strong> - Focus on time you have now rather than dwelling on future loss</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Express your feelings</strong> - Talk, write, or create art about what you're experiencing</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Say what needs to be said</strong> - Use this time for important conversations and closure</span>
              </p>
            </div>
            
            <div className="space-y-3">
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Seek support</strong> - Join caregiver support groups or counseling</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Take care of yourself</strong> - Self-care isn't selfish when caregiving</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Create memories</strong> - Photos, videos, letters for the future</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="text-2xl">✓</span>
                <span><strong>Be patient with yourself</strong> - There's no "right" way to handle this</span>
              </p>
            </div>
          </div>
        </div>

        {/* Important Note */}
        <div className="mt-12 bg-purple-50 border-2 border-purple-200 rounded-2xl p-6 text-center">
          <p className="text-lg text-gray-800 leading-relaxed">
            <strong>Remember:</strong> Experiencing anticipatory grief doesn't mean you'll grieve less after the loss occurs. 
            Both forms of grief are separate experiences, each deserving of time, space, and compassion.
          </p>
        </div>
      </div>
    </section>
  );
};

export default AnticipatorGrief;
